import { useState } from 'react';
import { FinancePageLayout } from '@/components/finance/FinancePageLayout';
import { BusinessUnitSelector, type BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import { BrandSelector, type Brand } from '@/components/finance/BrandSelector';
import { CashFlowRealized, CashFlowProjected, CashFlowChart, PaymentMetricsCard } from '@/components/finance/cashflow';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RefreshCw, Wallet } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

export default function CashFlow() {
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [brand, setBrand] = useState<Brand>('all');
  const [view, setView] = useState<'realizado' | 'projetado'>('projetado');
  const [period, setPeriod] = useState('30');
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
  };

  return (
    <FinancePageLayout>
      
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Fluxo de Caixa</h1>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={view} onValueChange={(v) => setView(v as 'realizado' | 'projetado')}>
            <TabsList>
              <TabsTrigger value="realizado">Realizado</TabsTrigger>
              <TabsTrigger value="projetado">Projetado</TabsTrigger>
            </TabsList>
          </Tabs>

          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {view === 'realizado' ? (
                <>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="60">Últimos 60 dias</SelectItem>
                  <SelectItem value="90">Últimos 90 dias</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="30">Próximos 30 dias</SelectItem>
                  <SelectItem value="60">Próximos 60 dias</SelectItem>
                  <SelectItem value="90">Próximos 90 dias</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>

          <div className="flex-1" />

          <BusinessUnitSelector value={businessUnit} onChange={setBusinessUnit} />
          <BrandSelector value={brand} onChange={setBrand} />
          
          <Button variant="outline" size="icon" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6">
        {/* Chart - always visible */}
        <CashFlowChart businessUnit={businessUnit} days={parseInt(period)} />

        {/* Conditional content based on view */}
        {view === 'realizado' ? (
          <CashFlowRealized 
            businessUnit={businessUnit} 
            brand={brand} 
            days={parseInt(period)} 
          />
        ) : (
          <>
            {/* Payment Metrics Card - only in projected view */}
            <PaymentMetricsCard businessUnit={businessUnit} brand={brand} />
            
            <CashFlowProjected 
              businessUnit={businessUnit} 
              brand={brand} 
              weeks={Math.ceil(parseInt(period) / 7)} 
            />
          </>
        )}
      </div>
    </FinancePageLayout>
  );
}
